import { useMemo } from 'react';
import { CallRecordingSetting } from '@weave/schema-gen-ts/dist/schemas/phone/call-config/v2/call_config.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueries } from 'react-query';
import { CallRecordingApi, CallRecordingQueryKeys } from '@frontend/api-call-recording';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';

export const useCallRecordingEnabled = (locationIds: string[]) => {
  const { accessibleLocationData } = useAppScopeStore();

  const tenantToLocationsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    locationIds.forEach((id) => {
      const tenantId = accessibleLocationData[id]?.phoneTenantId;
      if (tenantId) {
        if (!map[tenantId]) map[tenantId] = [];
        map[tenantId].push(id);
      }
    });
    return map;
  }, [locationIds, accessibleLocationData]);

  const uniqueTenantIds = useMemo(() => Object.keys(tenantToLocationsMap), [tenantToLocationsMap]);

  const recordingQueries = useQueries(
    uniqueTenantIds.map((tenantId) => ({
      queryKey: CallRecordingQueryKeys.listCallRecording(tenantId),
      queryFn: () => CallRecordingApi.GetCallRecording({ tenantId }),
      retry: false,
    }))
  );

  const isRecordingSettingsLoading = recordingQueries.some((q) => q.isLoading);

  const locationRecordingMap = useMemo(
    () =>
      uniqueTenantIds.reduce<Record<string, boolean>>((map, tenantId, idx) => {
        const enabled = recordingQueries[idx]?.data?.setting !== CallRecordingSetting.NONE;
        tenantToLocationsMap[tenantId].forEach((locId) => {
          map[locId] = enabled;
        });
        return map;
      }, {}),
    [uniqueTenantIds, recordingQueries, tenantToLocationsMap]
  );

  const hasCallRecordingWriteAccess = useMemo(() => {
    return locationIds.some(
      (locId) =>
        hasSchemaACL(locId, Permission.CALL_RECORDING_SETTING_WRITE) &&
        hasSchemaACL(locId, Permission.CALL_INTELLIGENCE_READ)
    );
  }, [locationIds]);

  const hasAnyRecordingsDisabled = useMemo(() => {
    return locationIds.some((locId) => locationRecordingMap[locId] === false);
  }, [locationIds, locationRecordingMap]);

  return {
    locationRecordingMap,
    isRecordingSettingsLoading,
    hasCallRecordingWriteAccess,
    hasAnyRecordingsDisabled,
  };
};
